import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useSEO } from '../../utils/seo';
import CalculatorSlider from './components/CalculatorSlider';
import ResultCard from './components/ResultCard';
import ROIBreakdown from './components/ROIBreakdown';
import PlanRecommendation from './components/PlanRecommendation';

export default function PricingCalculatorPage() {
  // SEO Configuration
  useSEO({
    title: 'ROI Calculator for Tradies | Calculate Your Savings | MtaPlannerPro Australia',
    description: 'Calculate your potential ROI with MtaPlannerPro. See how much time and money you\'ll save with AI quoting, automated invoicing, and GPS tracking. Interactive calculator shows extra revenue, time savings, and break-even analysis for Australian tradies.',
    keywords: 'tradie ROI calculator, construction software savings, quote software ROI, invoice automation savings, time savings calculator, business efficiency calculator, tradie profit calculator Australia',
    canonical: '/pricing-calculator',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'MtaPlannerPro ROI Calculator',
      applicationCategory: 'FinanceApplication',
      description: 'Interactive ROI calculator for Australian tradies to estimate time savings, extra revenue, and return on investment from using MtaPlannerPro software',
      url: `${import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com'}/pricing-calculator`,
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'AUD',
        description: 'Free ROI calculation tool'
      },
      featureList: [
        'Calculate time savings per week',
        'Estimate extra monthly revenue',
        'See break-even timeline',
        'Compare with current tools',
        'Personalized plan recommendations',
        'Annual ROI projections'
      ]
    }
  });

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: calcRef, isVisible: calcVisible } = useScrollAnimation();
  const { ref: breakdownRef, isVisible: breakdownVisible } = useScrollAnimation();
  const { ref: compareRef, isVisible: compareVisible } = useScrollAnimation();

  // Slider state
  const [jobsPerMonth, setJobsPerMonth] = useState(20);
  const [avgJobValue, setAvgJobValue] = useState(3000);
  const [hoursOnAdmin, setHoursOnAdmin] = useState(15);
  const [hourlyRate, setHourlyRate] = useState(85);
  const [currentToolCost, setCurrentToolCost] = useState(0);

  // Derived calculations
  const results = useMemo(() => {
    const mtaMonthlyCost = jobsPerMonth <= 5 ? 0 : jobsPerMonth <= 30 ? 39 : 79;
    const timeSavedWeekly = Math.round(hoursOnAdmin * 0.7);
    const timeSavedMonthly = timeSavedWeekly * 4;
    const extraJobsPerMonth = Math.round(timeSavedMonthly / 3);
    const extraMonthlyRevenue = extraJobsPerMonth * avgJobValue;
    const quoteWinRateBoost = Math.min(35, Math.round(jobsPerMonth * 1.2));
    const fasterPaymentDays = 18;
    const timeSavingsValue = timeSavedMonthly * hourlyRate;
    const totalMonthlyBenefit = extraMonthlyRevenue + timeSavingsValue;
    const netMonthlyProfit = totalMonthlyBenefit - mtaMonthlyCost;
    const annualROI = netMonthlyProfit * 12;
    const roiMultiplier = mtaMonthlyCost > 0 ? Math.round(totalMonthlyBenefit / mtaMonthlyCost) : 0;
    const breakEvenDays = mtaMonthlyCost > 0 ? Math.max(1, Math.round((mtaMonthlyCost / totalMonthlyBenefit) * 30)) : 0;
    const oldToolSavings = currentToolCost > 0 ? currentToolCost - mtaMonthlyCost : 0;

    return {
      mtaMonthlyCost,
      timeSavedWeekly,
      timeSavedMonthly,
      extraJobsPerMonth,
      extraMonthlyRevenue,
      quoteWinRateBoost,
      fasterPaymentDays,
      timeSavingsValue,
      totalMonthlyBenefit,
      netMonthlyProfit,
      annualROI,
      roiMultiplier,
      breakEvenDays,
      oldToolSavings,
    };
  }, [jobsPerMonth, avgJobValue, hoursOnAdmin, hourlyRate, currentToolCost]);

  const handleStartTrial = () => {
    window.REACT_APP_NAVIGATE?.('/demo');
  };

  // Before/After comparison data
  const comparisons = [
    {
      metric: 'Time to create a quote',
      before: '45–90 min',
      after: 'Under 60 sec',
      icon: 'ri-time-line',
      improvement: '98% faster',
    },
    {
      metric: 'Admin hours per week',
      before: `${hoursOnAdmin} hrs`,
      after: `${hoursOnAdmin - results.timeSavedWeekly} hrs`,
      icon: 'ri-calendar-check-line',
      improvement: `${results.timeSavedWeekly} hrs saved`,
    },
    {
      metric: 'Average payment time',
      before: '30+ days',
      after: `${30 - results.fasterPaymentDays} days`,
      icon: 'ri-bank-card-line',
      improvement: `${results.fasterPaymentDays} days faster`,
    },
    {
      metric: 'Quote win rate',
      before: '~40%',
      after: `~${40 + results.quoteWinRateBoost}%`,
      icon: 'ri-trophy-line',
      improvement: `+${results.quoteWinRateBoost}%`,
    },
    {
      metric: 'Jobs completed/month',
      before: `${jobsPerMonth}`,
      after: `${jobsPerMonth + results.extraJobsPerMonth}`,
      icon: 'ri-briefcase-line',
      improvement: `+${results.extraJobsPerMonth} extra`,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <i className="ri-tools-fill text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                MtaPlannerPro
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-amber-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <button
                onClick={handleStartTrial}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all shadow-md text-sm cursor-pointer whitespace-nowrap"
              >
                Start 7-Day Free Trial
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        ref={heroRef}
        className="pt-28 pb-12 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-orange-500 rounded-full blur-[150px]"></div>
        </div>

        <div
          className={`max-w-4xl mx-auto px-6 text-center relative z-10 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/15 border border-amber-500/25 rounded-full mb-6">
            <i className="ri-calculator-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-semibold">Interactive ROI Calculator</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            See Exactly How Much
            <span className="block bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              You&apos;ll Save &amp; Earn
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Adjust the sliders to match your business. Watch your potential savings, extra revenue, and ROI update in real time.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { icon: 'ri-user-line', value: '6,200+', label: 'Tradies Trust Us' },
              { icon: 'ri-star-fill', value: '4.9/5', label: 'Average Rating' },
              { icon: 'ri-money-dollar-circle-line', value: '$2.4M+', label: 'Revenue Generated' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
                <i className={`${s.icon} text-amber-400`}></i>
                <span className="text-white font-bold text-sm">{s.value}</span>
                <span className="text-slate-400 text-xs">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Calculator */}
      <section ref={calcRef} className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div
            className={`grid lg:grid-cols-5 gap-8 transition-all duration-700 ${calcVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Left: Sliders (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-7 shadow-xl border border-slate-100">
                <div className="flex items-center gap-2.5 mb-7">
                  <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <i className="ri-settings-3-line text-white text-lg"></i>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">Your Business Details</h2>
                </div>

                <div className="space-y-7">
                  <CalculatorSlider
                    label="Jobs per month"
                    value={jobsPerMonth}
                    min={2}
                    max={80}
                    icon="ri-briefcase-line"
                    suffix="jobs"
                    onChange={setJobsPerMonth}
                    color="amber"
                  />
                  <CalculatorSlider
                    label="Average job value"
                    value={avgJobValue}
                    min={500}
                    max={15000}
                    step={250}
                    icon="ri-money-dollar-circle-line"
                    suffix=""
                    prefix="$"
                    onChange={setAvgJobValue}
                    color="emerald"
                  />
                  <CalculatorSlider
                    label="Admin hours per week"
                    value={hoursOnAdmin}
                    min={2}
                    max={40}
                    icon="ri-time-line"
                    suffix="hrs"
                    onChange={setHoursOnAdmin}
                    color="orange"
                  />
                  <CalculatorSlider
                    label="Your hourly rate"
                    value={hourlyRate}
                    min={40}
                    max={200}
                    step={5}
                    icon="ri-wallet-3-line"
                    suffix="/hr"
                    prefix="$"
                    onChange={setHourlyRate}
                    color="amber"
                  />
                  <CalculatorSlider
                    label="Current tool cost"
                    value={currentToolCost}
                    min={0}
                    max={300}
                    step={10}
                    icon="ri-apps-line"
                    suffix="/mo"
                    prefix="$"
                    onChange={setCurrentToolCost}
                    color="emerald"
                  />
                </div>

                {/* Quick Presets */}
                <div className="mt-7 pt-6 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Presets</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Solo Tradie', jobs: 10, value: 2000, admin: 10, rate: 75 },
                      { label: 'Growing Biz', jobs: 25, value: 3500, admin: 20, rate: 90 },
                      { label: 'Busy Team', jobs: 50, value: 5000, admin: 30, rate: 110 },
                    ].map((preset, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setJobsPerMonth(preset.jobs);
                          setAvgJobValue(preset.value);
                          setHoursOnAdmin(preset.admin);
                          setHourlyRate(preset.rate);
                        }}
                        className="px-3.5 py-2 bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-lg text-xs font-semibold text-slate-600 hover:text-amber-700 transition-all cursor-pointer whitespace-nowrap"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Plan Recommendation */}
              <div className="bg-white rounded-2xl p-7 shadow-xl border border-slate-100">
                <PlanRecommendation jobsPerMonth={jobsPerMonth} onStartTrial={handleStartTrial} />
              </div>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              {/* Big ROI Card */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[80px]"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/10 rounded-full blur-[60px]"></div>

                <div className="relative z-10">
                  {/* Main Headline Result */}
                  <div className="text-center mb-8">
                    <p className="text-slate-400 text-sm font-medium mb-2">Your Potential Annual ROI</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent tabular-nums">
                        ${results.annualROI.toLocaleString()}
                      </span>
                      <span className="text-slate-500 text-lg font-medium">/year</span>
                    </div>
                    {results.breakEvenDays > 0 && (
                      <p className="text-emerald-400 text-sm mt-2 font-medium">
                        <i className="ri-flashlight-line mr-1"></i>
                        Break-even in just {results.breakEvenDays} day{results.breakEvenDays !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>

                  {/* Result Cards Grid */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    <ResultCard
                      icon="ri-money-dollar-circle-line"
                      label="Extra Monthly Revenue"
                      value={results.extraMonthlyRevenue}
                      prefix="$"
                      sublabel={`From ${results.extraJobsPerMonth} additional jobs`}
                      iconBg="bg-amber-500/20"
                      iconColor="text-amber-400"
                    />
                    <ResultCard
                      icon="ri-time-line"
                      label="Time Saved Weekly"
                      value={results.timeSavedWeekly}
                      suffix=" hrs"
                      sublabel={`Worth $${results.timeSavingsValue.toLocaleString()}/mo`}
                      iconBg="bg-emerald-500/20"
                      iconColor="text-emerald-400"
                    />
                    <ResultCard
                      icon="ri-trophy-line"
                      label="Quote Win Rate Boost"
                      value={results.quoteWinRateBoost}
                      suffix="%"
                      sublabel="More jobs won with faster quotes"
                      iconBg="bg-sky-500/20"
                      iconColor="text-sky-400"
                    />
                    <ResultCard
                      icon="ri-rocket-line"
                      label="Extra Jobs per Month"
                      value={results.extraJobsPerMonth}
                      prefix="+"
                      sublabel={`At $${avgJobValue.toLocaleString()} avg value`}
                      iconBg="bg-orange-500/20"
                      iconColor="text-orange-400"
                    />
                  </div>

                  {/* ROI Breakdown Bars */}
                  <ROIBreakdown
                    currentCost={currentToolCost}
                    mtaCost={results.mtaMonthlyCost}
                    timeSavedHours={results.timeSavedMonthly}
                    extraJobs={results.extraJobsPerMonth}
                    extraRevenue={results.extraMonthlyRevenue}
                    hourlyRate={hourlyRate}
                  />

                  {/* CTA */}
                  <div className="mt-8 flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={handleStartTrial}
                      className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap text-center"
                    >
                      Start 7-Day Free Trial
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                    <Link
                      to="/#pricing"
                      className="flex-1 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap text-center"
                    >
                      View All Plans
                    </Link>
                  </div>
                </div>
              </div>

              {/* Tool Cost Savings (if applicable) */}
              {currentToolCost > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-exchange-dollar-line text-emerald-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-emerald-800 mb-1">Switching Saves You More</h4>
                      <p className="text-sm text-emerald-700">
                        You&apos;re currently paying <strong>${currentToolCost}/mo</strong> for your tools.
                        MtaPlannerPro at <strong>${results.mtaMonthlyCost}/mo</strong> saves you{' '}
                        <strong>${results.oldToolSavings > 0 ? results.oldToolSavings : 0}/mo</strong> on
                        software alone — plus all the extra revenue from faster quoting.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After Comparison */}
      <section ref={compareRef} className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div
            className={`text-center mb-12 transition-all duration-700 ${compareVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-semibold mb-4">
              <i className="ri-arrow-left-right-line"></i>
              Before vs After
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Your Business Transformation
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Based on your numbers, here&apos;s how MtaPlannerPro changes your day-to-day
            </p>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-200 ${compareVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {comparisons.map((item, i) => (
              <div
                key={i}
                className="bg-white border border-slate-100 rounded-xl p-5 hover:shadow-lg hover:border-amber-200 transition-all duration-300 group"
              >
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Icon + Metric */}
                  <div className="col-span-12 sm:col-span-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 group-hover:bg-amber-100 rounded-lg flex items-center justify-center transition-colors shrink-0">
                      <i className={`${item.icon} text-slate-500 group-hover:text-amber-600 text-lg transition-colors`}></i>
                    </div>
                    <span className="font-semibold text-slate-800 text-sm">{item.metric}</span>
                  </div>

                  {/* Before */}
                  <div className="col-span-4 sm:col-span-2 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Before</p>
                    <p className="text-base font-bold text-slate-500 line-through decoration-red-400">{item.before}</p>
                  </div>

                  {/* Arrow */}
                  <div className="col-span-1 sm:col-span-1 flex justify-center">
                    <i className="ri-arrow-right-line text-amber-500 text-lg"></i>
                  </div>

                  {/* After */}
                  <div className="col-span-4 sm:col-span-2 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">After</p>
                    <p className="text-base font-bold text-emerald-600">{item.after}</p>
                  </div>

                  {/* Improvement Badge */}
                  <div className="col-span-3 sm:col-span-3 flex justify-end">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold whitespace-nowrap">
                      <i className="ri-arrow-up-circle-fill text-emerald-500"></i>
                      {item.improvement}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section ref={breakdownRef} className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50">
        <div
          className={`max-w-3xl mx-auto px-6 text-center transition-all duration-700 ${breakdownVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-white rounded-2xl p-10 shadow-xl border border-amber-100">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-2-line text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Ready to Earn ${results.annualROI.toLocaleString()} More?
            </h2>
            <p className="text-slate-600 mb-8 max-w-lg mx-auto">
              Join 6,200+ Australian tradies who are saving time, winning more jobs, and getting paid faster with MtaPlannerPro.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={handleStartTrial}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Start 7-Day Free Trial
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <Link
                to="/"
                className="px-8 py-4 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Back to Homepage
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-check-line text-emerald-500"></i>
                <span>5 free quotes included</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-emerald-500"></i>
                <span>Credit card required</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-close-circle-line text-emerald-500"></i>
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-money-dollar-circle-line text-emerald-500"></i>
                <span>30-day money-back guarantee</span>
              </div>
            </div>
          </div>

          {/* Footer Link */}
          <div className="mt-8 text-xs text-slate-400">
            <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="hover:text-amber-600 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
